import { useQuery } from "@tanstack/react-query";
import { apiFetch, getJson } from "../../lib/http";


const DASHBOARD_STALE = 5 * 60_000;

function readTotalItems(body: {
  meta?: { totalItems?: number };
  data?: { meta?: { totalItems?: number } };
  totalItems?: number;
}): number {
  return (
    body?.meta?.totalItems ??
    body?.data?.meta?.totalItems ??
    body?.totalItems ??
    0
  );
}

export interface V5Stats {
  users: number;
  devices: number;
  vehicles: number;
  companies: number;
}

export function useV5Stats() {
  return useQuery({
    queryKey: ["v5Stats"],
    staleTime: DASHBOARD_STALE,
    queryFn: async ({ signal }): Promise<V5Stats> => {
      const countParams = { limit: 1, page: 1 };
      const skip = { _skipCompanyScope: true } as never;
      const [users, devices, vehicles, companiesBody] = await Promise.all([
        getJson<Parameters<typeof readTotalItems>[0]>(
          "/userList",
          countParams,
          { signal },
          skip,
        )
          .then(readTotalItems)
          .catch(() => 0),
        getJson<Parameters<typeof readTotalItems>[0]>(
          "/devicesListForReports",
          countParams,
          { signal },
          skip,
        )
          .then(readTotalItems)
          .catch(() => 0),
        getJson<Parameters<typeof readTotalItems>[0]>(
          "/vehicleList",
          countParams,
          { signal },
          skip,
        )
          .then(readTotalItems)
          .catch(() => 0),
        getJson<{ data?: { data?: unknown[] } }>(
          "/companyListAll",
          undefined,
          { signal },
        ).catch(() => ({ data: { data: [] } })),
      ]);
      return {
        users,
        devices,
        vehicles,
        companies: companiesBody?.data?.data?.length ?? 0,
      };
    },
  });
}

export interface DailyHistoryPlatformStat {
  effectiveness_pct: number;
  reporting: number;
  not_reporting: number;
  total: number;
}

export interface DailyHistoryPoint {
  date: string;
  active_units: number;
  inactive_units: number;
  total_units: number;
  altas_count: number;
  bajas_count: number;
  altas_by_platform?: Record<string, number>;
  bajas_by_platform?: Record<string, number>;
  effectiveness_pct: number;
  effectiveness_avg_pct: number;
  effectiveness_peak_pct: number;
  reporting: number;
  not_reporting: number;
  companies_total: number;
  users_total: number;
  by_platform?: Record<string, DailyHistoryPlatformStat>;
  has_data?: boolean;
}

export interface DailyHistoryParams {
  year?: number;
  month?: number;
}

export function useDailyHistoryYears() {
  return useQuery({
    queryKey: ["dailyHistoryYears"],
    staleTime: DASHBOARD_STALE,
    queryFn: ({ signal }) =>
      getJson<{ data?: number[] }>("/dailyHistory/years", undefined, {
        signal,
      }).then((body) => body.data ?? []),
  });
}

export function useDailyHistory(params?: DailyHistoryParams) {
  const year = params?.year;
  const month = params?.month;
  return useQuery({
    queryKey: ["dailyHistory", year, month],
    staleTime: DASHBOARD_STALE,
    queryFn: ({ signal }) =>
      getJson<{ data?: DailyHistoryPoint[]; year?: number; month?: number; monthName?: string }>(
        "/dailyHistory/monthly-data",
        { year, month },
        { signal },
      ).then((body) => ({
        points: body.data ?? [],
        year: body.year,
        month: body.month,
        monthName: body.monthName,
      })),
    enabled: year != null && month != null,
  });
}

export interface DailyHistoryRangeParams {
  from?: string;
  to?: string;
}

export function useDailyHistoryRange(params?: DailyHistoryRangeParams) {
  const from = params?.from;
  const to = params?.to;
  return useQuery({
    queryKey: ["dailyHistoryRange", from, to],
    staleTime: DASHBOARD_STALE,
    queryFn: ({ signal }) =>
      getJson<{ data?: DailyHistoryPoint[]; from?: string; to?: string }>(
        "/dailyHistory/range",
        { from, to },
        { signal },
      ).then((body) => ({
        points: body.data ?? [],
        from: body.from,
        to: body.to,
      })),
    enabled: Boolean(from) && Boolean(to),
  });
}

export interface DeviceMovement {
  date: string;
  identifier: string;
  name: string;
  platform: string;
  type: string;
  status_name: string;
}

export function useDailyHistoryMovements(params?: { from?: string; to?: string }) {
  const from = params?.from;
  const to = params?.to;
  return useQuery({
    queryKey: ["dailyHistoryMovements", from, to],
    staleTime: DASHBOARD_STALE,
    enabled: Boolean(from) && Boolean(to),
    queryFn: ({ signal }) =>
      getJson<{ data?: DeviceMovement[] }>(
        "/dailyHistory/movements",
        { from, to },
        { signal },
      ).then((body) => body.data ?? []),
  });
}

export function useDeviceReporting24h() {
  return useQuery({
    queryKey: ["deviceReporting24h"],
    staleTime: DASHBOARD_STALE,
    queryFn: ({ signal }) =>
      getJson<{ data?: any[] }>("/deviceReporting24h", undefined, {
        signal,
      }).then((body) => body.data ?? []),
  });
}

export interface MaponDashboardData {
  units: any[];
  users: any[];
}

export function useMaponDashboard() {
  return useQuery({
    queryKey: ["maponDashboard"],
    staleTime: DASHBOARD_STALE,
    queryFn: async ({ signal }): Promise<MaponDashboardData> => {
      let units: any[] = [];
      let users: any[] = [];

      const unitsRes = await apiFetch(`/mapon/devices-with-companies`, {
        signal,
      });
      if (unitsRes.ok) {
        const result = await unitsRes.json();
        if (result.meta && !result.meta.error && result.data?.units) {
          units = result.data.units;
        }
      }

      const usersRes = await apiFetch(`/mapon/users`, { signal });
      if (usersRes.ok) {
        const result = await usersRes.json();
        if (result.meta && !result.meta.error && result.data) {
          users =
            result.data?.data?.users ||
            result.data?.users ||
            (Array.isArray(result.data) ? result.data : []);
        }
      }

      return { units, users };
    },
  });
}

export interface WialonDashboardData {
  devicesCount: number;
  devicesData: any[];
  usersCount: number;
  usersData: any[];
  criticalServiceData: any[];
  tokenError: boolean;
  wialonError: boolean;
}

export function useWialonDashboard(token: string) {
  return useQuery({
    queryKey: ["wialonDashboard", token],
    staleTime: DASHBOARD_STALE,
    queryFn: async ({ signal }): Promise<WialonDashboardData> => {
      const headers: Record<string, string> = {};
      if (token) headers["x-wialon-token"] = token;

      const out: WialonDashboardData = {
        devicesCount: 0,
        devicesData: [],
        usersCount: 0,
        usersData: [],
        criticalServiceData: [],
        tokenError: false,
        wialonError: false,
      };

      const isWialonError7 = (e: any) =>
        e?.error === 7 || e?.data?.error === 7 || e?.meta?.error === 7;

      const devicesRes = await apiFetch(
        `/wialon/devices-with-companies`,
        { headers, signal },
      );
      if (devicesRes.ok) {
        const result = await devicesRes.json();
        if (result.meta && !result.meta.error && result.data) {
          const devices = result.data.items || [];
          out.devicesCount =
            result.data.totalItemsCount ||
            result.data.pagination?.totalItems ||
            devices.length ||
            0;
          out.devicesData = devices;
        }
      } else {
        const err = await devicesRes.json();
        if (err.data?.message?.includes("token")) out.tokenError = true;
        if (isWialonError7(err)) out.wialonError = true;
      }

      const usersRes = await apiFetch(`/wialon/users?all=true`, {
        headers,
        signal,
      });
      if (usersRes.ok) {
        const result = await usersRes.json();
        if (result.meta && !result.meta.error && result.data) {
          const users = result.data.items || [];
          out.usersCount =
            result.data.totalItemsCount ||
            result.data.pagination?.totalItems ||
            users.length ||
            0;
          out.usersData = users;
        }
      } else {
        const err = await usersRes.json();
        if (err.data?.message?.includes("token")) out.tokenError = true;
        if (isWialonError7(err)) out.wialonError = true;
      }

      const criticalRes = await apiFetch(
        `/wialon/critical-service-data`,
        { headers, signal },
      );
      if (criticalRes.ok) {
        const result = await criticalRes.json();
        if (result.meta && !result.meta.error) {
          out.criticalServiceData = Array.isArray(result.data)
            ? result.data
            : [];
        }
      } else {
        const err = await criticalRes.json();
        if (isWialonError7(err)) out.wialonError = true;
      }

      return out;
    },
  });
}

export interface ReportingTrendPoint {
  date: string;
  reportingUnits: number;
  totalUnits: number;
  percentage: number;
}

interface ReportingTrendBody {
  data?: {
    data?: ReportingTrendPoint[];
  };
}

export function useReportingTrend(range: "7" | "30" | "90") {
  return useQuery({
    queryKey: ["reportingTrend", range],
    queryFn: ({ signal }) =>
      getJson<ReportingTrendBody>(
        "/reportingTrend",
        { period: `${range}d` },
        { signal },
      ).then((body) =>
        Array.isArray(body.data?.data)
          ? (body.data!.data as ReportingTrendPoint[])
          : [],
      ),
    staleTime: 5 * 60_000,
  });
}
